import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import { Margin } from '@nib/layout';
import styled from 'styled-components';
import PaymentMethod from '../payment-method';
import NestedFormFields from '../nested-form-fields';
import AccountName from '../account-name';
import AccountNumber from '../account-number';
import Bsb from '../bsb';
import DirectDebitServiceAgreement from '../direct-debit-service-agreement';
import { formatDecimal } from '../../util/format-decimal';
import { AddFunnelPropFromContext } from '../funnel-context';
import { getGroupEventHandlerProps } from '../../util/propTypes';
import Alert from '@nib-components/alert';
import Link from '@nib-components/link';
import { INTERNATIONAL_FUNNELS, PHONE_NUMBERS, FUNNELS, EMAIL } from '../../util/constants';
import { Stack } from '@nib/layout';
import Copy from '@nib-components/copy';
import { InfoSystemIcon } from '@nib/icons';
import { linkLoud } from '@nib-components/theme';
import CreditCardSection from '../credit-card-section';
import { StandardInfoBox } from '../info-box';
export const CopyWithColoredLink = styled(Copy)`
  & > a {
    color: ${linkLoud};
  }
`;
export const createMarkup = mainText => {
  return {
    __html: mainText
  };
};
const getPaymentDiscountMessage = (funnel, braintree) => {
  var _a;
  if (funnel.toUpperCase() === FUNNELS.PRICELINE) {
    const memberServices = 'https://health.priceline.com.au/';
    return `4% Direct Debit discount: Set up a direct debit from your bank, building society or credit union cheque or savings account for a discount of 4% off your premiums when you pay by direct debit and are an active Priceline Sister Club member. You can change or update your direct debit details by logging in to <a href='${memberServices}' target='_blank'>member services</a>, going to 'My Policy' and selecting 'Payments'.`;
  }
  return `Paying by direct debit from a cheque or savings account gives you a 4% discount. If you pay by credit card${((_a = braintree === null || braintree === void 0 ? void 0 : braintree.options) === null || _a === void 0 ? void 0 : _a.paypal) ? ' or PayPal' : ''}, you won't receive this discount.`;
};
const InfoSection = props => {
  const {
    funnel = FUNNELS.ARHI,
    paymentMethodCopyContent,
    newPrice,
    hasMethodChanged,
    braintree = {},
    showDirectDebitDiscountMessage
  } = props;
  const showPriceNotification = !INTERNATIONAL_FUNNELS.includes(funnel);
  const price = newPrice === null ? 0 : newPrice;
  if (showPriceNotification) {
    const priceInfoBoxContent = paymentMethodCopyContent && paymentMethodCopyContent.priceChangeCopy || React.createElement(React.Fragment, null, "Your price has been updated to reflect your changed payment method. ", React.createElement("strong", null, "New price $", formatDecimal(price, {
      places: 2,
      separator: ','
    }), "*."));
    return React.createElement(Stack, {
      space: 2
    }, showDirectDebitDiscountMessage && React.createElement(StandardInfoBox, {
      withTip: true
    }, React.createElement(CopyWithColoredLink, {
      dangerouslySetInnerHTML: createMarkup(getPaymentDiscountMessage(funnel, braintree))
    })), hasMethodChanged && newPrice && React.createElement(StandardInfoBox, {
      role: "status",
      withTip: !showDirectDebitDiscountMessage
    }, React.createElement(Copy, null, priceInfoBoxContent)));
  } else {
    return null;
  }
};
const DirectDebitSection = props => {
  const {
    values,
    eventHandlers,
    showDirectDebitServiceAgreement,
    links,
    bankName
  } = props;
  return React.createElement(React.Fragment, null, React.createElement(Field, {
    name: "accountName",
    id: "accountName",
    component: AccountName,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'accountName'
    })
  }), React.createElement(Field, {
    name: "bsb",
    id: "bsb",
    label: "BSB",
    component: Bsb,
    bankName: bankName,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'bsb'
    })
  }), React.createElement(Field, {
    name: "accountNumber",
    id: "accountNumber",
    component: AccountNumber,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'accountNumber'
    })
  }), showDirectDebitServiceAgreement && React.createElement(DirectDebitServiceAgreement, {
    id: "directDebitAgreement",
    name: "directDebitAgreement",
    values: values,
    links: links,
    paymentMethod: "DirectDebit",
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'directDebitAgreement'
    })
  }));
};
const PaymentMethodGroup = props => {
  const {
    id = 'paymentMethod',
    name = 'paymentMethod',
    funnel = 'IWHI',
    groupOnBlurHandlers = {},
    groupOnChangeHandlers = {},
    showDirectDebitDiscountMessage = true,
    paymentMethodLabel = 'Payment method',
    values = {},
    defaultMethod,
    newPrice,
    paymentMethodCopyContent,
    braintree,
    directDebitAgreementLinks,
    phoneNumber,
    help,
    bankName
  } = props;
  const showSubfields = values[id] !== '';
  const isDirectDebit = values[id] === 'DirectDebit';
  const isCreditCard = values[id] === 'CreditCard';
  const isGroup = values[id] === 'Group';
  const showDirectDebitServiceAgreement = !INTERNATIONAL_FUNNELS.includes(funnel);
  const [hasMethodChanged, setMethodChanged] = useState(false);
  const eventHandlers = {
    groupOnBlurHandlers,
    groupOnChangeHandlers
  };
  const phone = phoneNumber || PHONE_NUMBERS[funnel] || PHONE_NUMBERS.ARHI;
  const email = EMAIL[funnel] || EMAIL.ARHI;
  useEffect(() => {
    if (showDirectDebitServiceAgreement && !hasMethodChanged && values[id] !== defaultMethod) {
      setMethodChanged(true);
    }
  }, [values[id]]);
  const hrefTel = `tel:${phone.replace(/\s+/g, '')}`;
  return React.createElement("div", null, React.createElement(Field, {
    name: name,
    id: id,
    component: PaymentMethod,
    help: help,
    label: paymentMethodLabel || '',
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id
    }),
    options: paymentMethodCopyContent && paymentMethodCopyContent.paymentMethodLabels
  }), isCreditCard && (braintree === null || braintree === void 0 ? void 0 : braintree.errorMessage) && React.createElement(Margin, {
    vertical: 4
  }, React.createElement(Alert, {
    title: funnel === FUNNELS.IAP ? "We're experiencing technical issues." : braintree.errorMessage,
    type: "error",
    variation: "soft"
  }, funnel === FUNNELS.IAP ? React.createElement(Stack, {
    space: 2
  }, React.createElement(Copy, null, braintree.errorMessage), React.createElement(Copy, null, "Call", ' ', React.createElement(Link, {
    component: "a",
    href: hrefTel
  }, phone), ' ', "or email", ' ', React.createElement(Link, {
    component: "a",
    href: `mailto:${email}`
  }, email), ".")) : React.createElement(React.Fragment, null, "You can try refreshing the page, or call us on", ' ', React.createElement(Link, {
    component: "a",
    href: hrefTel
  }, phone), ' ', "if you continue to see this error."))), React.createElement(InfoSection, {
    funnel: funnel,
    newPrice: newPrice,
    paymentMethodCopyContent: paymentMethodCopyContent,
    hasMethodChanged: hasMethodChanged,
    braintree: braintree,
    isCreditCard: isCreditCard,
    showDirectDebitDiscountMessage: showDirectDebitDiscountMessage
  }), (isDirectDebit || isCreditCard) && React.createElement(NestedFormFields, {
    id: "paymentMethodGroupNestedFormFields",
    expanded: showSubfields && !(isCreditCard && (braintree === null || braintree === void 0 ? void 0 : braintree.errorMessage)),
    subHeading: isDirectDebit ? 'Direct debit request details' : isCreditCard && !braintree ? 'Credit card details' : undefined
  }, isDirectDebit && React.createElement(DirectDebitSection, {
    bankName: bankName,
    showDirectDebitServiceAgreement: showDirectDebitServiceAgreement,
    values: values,
    eventHandlers: eventHandlers,
    links: directDebitAgreementLinks
  }), isCreditCard && React.createElement(CreditCardSection, {
    showDirectDebitServiceAgreement: showDirectDebitServiceAgreement,
    values: values,
    eventHandlers: eventHandlers,
    braintree: braintree
  })), isGroup && React.createElement(StandardInfoBox, {
    withTip: true,
    icon: InfoSystemIcon
  }, "Please ensure you nominate your payment method in the OSHC Refund & Cancellation form."));
};
PaymentMethodGroup.defaultProps = {
  id: 'paymentMethod',
  name: 'paymentMethod',
  funnel: 'IWHI',
  groupOnBlurHandlers: {},
  groupOnChangeHandlers: {},
  showDirectDebitDiscountMessage: true,
  paymentMethodLabel: 'Payment method',
  bankName: ''
};
const PaymentMethodGroupWithFunnel = props => React.createElement(AddFunnelPropFromContext, null, React.createElement(PaymentMethodGroup, {
  ...props
}));
export default PaymentMethodGroupWithFunnel;